import fileDownload from 'js-file-download';
import React, { useRef, useState, useEffect } from 'react';
import DocViewer, { IDocument, DocViewerRenderers } from '@cyntler/react-doc-viewer';

import { LoadingButton } from '@mui/lab';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ShieldIcon from '@mui/icons-material/Shield';
import { Box, Button, useTheme, IconButton, Typography } from '@mui/material';

import { bulkDownload } from 'src/services/dlxDriveService';
import { DLXDriveBulkDownloadModelType } from 'src/@types/dlx-drive/types';

import Iconify from 'src/components/Iconify';

import 'src/sections/shared/file-viewer.css';

import { fLocalDateTime } from '../formatTime';
import useLocales from '../../hooks/useLocales';
import { useSelector } from '../../redux/store';
import useResponsive from '../../hooks/useResponsive';
import viewerFileDownload from './viewerFileDownload';
import useNotifications from '../../hooks/useNotifications';
import documentService from '../../services/documentService';
import encryptIDWithRsaPublicKey from './encryptIDWithRsaPublicKey';
import { confirmDownload } from '../../redux/slices/documentHistory';
import DocumentsViewerPDFRenderer from './DocumentsViewerPDFRenderer';
import { FileExtensions } from '../../@types/fileView/enums/fileExtensions';
import { ALLOWED_FILES_FOR_VALIDATION } from '../../sections/documents/upload/utils';
import { DOC_VIEWER_DOCUMENT_EXTENSIONS } from '../constants/documentsViewerConstants';
import DocumentsViewerNavigation from '../../sections/documents/DocumentsViewerNavigation';
import DocumentFileViewerSkeleton from '../../sections/documents/DocumentFileViewerSkeleton';
import DocumentValidationReportModal from '../../sections/documents/DocumentValidationReportModal';
import DocumentAdocValidationReportModal from '../../sections/documents/DocumentAdocValidationReportModal';
import {
  ValidationData,
  AdocValidationData,
  ValidationDataOnStart,
} from '../../@types/documents/validationReport';

// --------------------------------------------------------

type DocumentsViewerProps = {
  docs: IDocument[];
  isLoading: boolean;
  setIsLoading: (isLoading: boolean) => void;
  showNavigation: boolean;
  isZooming: boolean;
  setIsZooming: (isZooming: boolean) => void;
};

const DocumentsViewer: React.FC<DocumentsViewerProps> = ({
  docs,
  isLoading,
  setIsLoading,
  showNavigation,
  isZooming,
  setIsZooming,
}) => {
  const { t } = useLocales();
  const isMobile = useResponsive('down', 'sm');
  const { enqueueSuccess, enqueueError } = useNotifications();
  const { document } = useSelector((state) => state.document);
  const [currentDocument, setCurrentDocument] = useState<IDocument>();
  const [currentDocumentIndex, setCurrentDocumentIndex] = useState<number>(0);
  const [validationData, setValidationData] = useState<ValidationData>();
  const [adocValidationData, setAdocValidationData] = useState<AdocValidationData>();
  const [validationDataOnStart, setValidationDataOnStart] = useState<ValidationDataOnStart>();
  const [validationIsLoading, setValidationIsLoading] = useState(false);

  const wrapper = useRef<HTMLDivElement>(null);
  const [wrapperSize, setWrapperSize] = useState<{ width: number; height: number }>({
    width: 0,
    height: 0,
  });
  const theme = useTheme();

  const handleDocumentChange = async (document: IDocument) => {
    const newActiveDocument = { ...document };
    newActiveDocument.uri = `${encryptIDWithRsaPublicKey(newActiveDocument.uri)}`;

    setCurrentDocument(newActiveDocument);
  };

  const handleWindowResize = () => {
    if (wrapper.current) {
      const width = wrapper.current.offsetWidth;
      const height = wrapper.current.offsetWidth * 1.41;

      if (width !== wrapperSize.width || height !== wrapperSize.height) {
        setWrapperSize({ width, height });
      }
    }
  };

  const handleContainerDownload = async (id: number[]) => {
    const model: DLXDriveBulkDownloadModelType = {
      folderIds: [],
      documentIds: id,
    };

    try {
      const file = await bulkDownload(model);
      if (!document) return;

      const fileName = `${file.fileName}`;

      fileDownload(file.file, fileName);
      enqueueSuccess('dlxDrive.notifications.downloadSuccess');
      await confirmDownload(document?.id || null);
    } catch (error) {
      enqueueError('dlxDrive.errors.downloadError');
    }
  };

  const container = [
    FileExtensions.Adoc,
    FileExtensions.Asic,
    FileExtensions.Bdoc,
    FileExtensions.Edoc,
  ];
  const isContainer = (extension: string) => container.includes(extension as FileExtensions);

  const handleFileDownload = async () => {
    // single file download
    if (currentDocument && document && !isContainer(document.extension)) {
      try {
        await viewerFileDownload(currentDocument);

        enqueueSuccess(t('documents.snackbar.documentSuccessfullyDownloaded'), {
          documentName: currentDocument.fileName,
        });

        await confirmDownload(document?.id || null);
      } catch (error) {
        enqueueError(t('documents.snackbar.errorDownloadingDocument'), {
          documentName: currentDocument.fileName,
        });
      }
    }
    // container download
    if (document && isContainer(document.extension)) {
      await handleContainerDownload([document.id]);
    }
  };

  const handleDocumentValidationById = async (id: number) => {
    if (document) {
      setValidationDataOnStart(undefined);
      setValidationIsLoading(true);

      try {
        const extension = (document.extension as FileExtensions) || FileExtensions.Pdf;

        const data = await documentService.validateDocumentById([id]);

        if (extension === FileExtensions.Adoc) {
          setAdocValidationData(data[`${id}`]);
        } else {
          setValidationData(data[`${id}`]);
        }

        enqueueSuccess(t('documents.snackbar.documentSuccessfullyValidated'), {
          documentName: document.name,
        });

        if (extension === FileExtensions.Adoc) {
          const newValidationDataOnStart: ValidationDataOnStart = {
            validationResult: data[`${id}`].validationResult,
            validationTime: data[`${id}`].adocSimpleReport.validationTime,
          };

          setValidationDataOnStart(newValidationDataOnStart);
        } else {
          const newValidationDataOnStart: ValidationDataOnStart = {
            validationResult: data[`${id}`].validationResult,
            validationTime: data[`${id}`].simpleReport.validationTime,
          };

          setValidationDataOnStart(newValidationDataOnStart);
        }
      } catch (error) {
        enqueueError(t('documents.snackbar.documentValidationFailed'), {
          documentName: document.name,
        });
      } finally {
        setValidationIsLoading(false);
      }
    }
  };

  const handleValidationReportClose = () => {
    setValidationData(undefined);
    setAdocValidationData(undefined);
  };

  useEffect(() => {
    setCurrentDocumentIndex(0);

    if (docs.length > 0) {
      handleDocumentChange(docs[0]).then();
    }
  }, [docs]);

  useEffect(() => {
    setIsLoading(true);
    handleDocumentChange(docs[currentDocumentIndex]).then();
  }, [currentDocumentIndex]);

  useEffect(() => {
    handleWindowResize();
  }, [wrapper.current, isZooming]);

  useEffect(() => {
    if (document && document.validationTime !== null) {
      const date = new Date(document.validationTime);

      const newValidationDataOnStart: ValidationDataOnStart = {
        validationResult: document.validationResult,
        validationTime: date.toLocaleString(),
      };

      setValidationDataOnStart(newValidationDataOnStart);
    }
  }, [document]);

  useEffect(() => {
    window.addEventListener('resize', handleWindowResize);

    return () => {
      window.removeEventListener('resize', handleWindowResize);
    };
  }, []);

  const isPdfRendererActive = !!(
    currentDocument?.fileType &&
    !DOC_VIEWER_DOCUMENT_EXTENSIONS.includes(currentDocument.fileType)
  );

  return (
    <>
      {!isZooming && (
        <Box sx={{ display: 'flex', justifyContent: 'left', alignItems: 'center', gap: 2, my: 2, flexWrap: 'wrap' }}>
          <Button
            onClick={handleFileDownload}
            type="button"
            variant="contained"
            color="secondary"
            startIcon={<Iconify icon="eva:download-fill" width={22} height={22} />}
            id="ovjbyt9b"
          >
            {t('documents.buttons.download')}
          </Button>

          {document && ALLOWED_FILES_FOR_VALIDATION.includes(document.extension) && (
            <LoadingButton
              onClick={() => {
                handleDocumentValidationById(document ? document.id : -1);
              }}
              loading={validationIsLoading}
              type="button"
              variant="outlined"
              color="secondary"
              id="vks4aqks"
              startIcon={<ShieldIcon fontSize="small" />}
            >
              {t('documents.buttons.validate')}
            </LoadingButton>
          )}

          {validationDataOnStart && (
            <Box>
              {validationDataOnStart.validationResult === 0 && (
                <Typography color={theme.palette.error.main} sx={{ verticalAlign: 'center' }}>
                  <Iconify mb="-6px" width={24} height={24} icon="eva:close-circle-outline" />
                  {` ${t('documents.validationReport.invalid')} (${t(
                    'documents.validationReport.validationTime'
                  ).toLowerCase()}: ${fLocalDateTime(validationDataOnStart.validationTime)})`}
                </Typography>
              )}

              {validationDataOnStart.validationResult === 1 && (
                <Typography color={theme.palette.success.main} sx={{ verticalAlign: 'center' }}>
                  <Iconify mb="-6px" width={24} height={24} icon="eva:checkmark-circle-2-outline" />
                  {` ${t('documents.validationReport.valid')} (${t(
                    'documents.validationReport.validationTime'
                  ).toLowerCase()}: ${fLocalDateTime(validationDataOnStart.validationTime)})`}
                </Typography>
              )}

              {validationDataOnStart.validationResult === 2 && (
                <Typography color="secondary" sx={{ verticalAlign: 'center' }}>
                  <Iconify mb="-6px" width={24} height={24} icon="eva:checkmark-circle-2-outline" />
                  {` ${t('documents.validationReport.noSignatures')} (${t(
                    'documents.validationReport.validationTime'
                  ).toLowerCase()}: ${fLocalDateTime(validationDataOnStart.validationTime)})`}
                </Typography>
              )}
            </Box>
          )}
        </Box>
      )}
      <Box sx={{ width: '100%', position: 'relative', boxShadow: isMobile ? 'none' : '2px 3px 4px #cccccc', borderRadius: isMobile && isZooming ? 0 : '16px', overflow: 'hidden' }}>
        {showNavigation && docs.length > 0 && !(isMobile && isZooming) && (
          <Box
            sx={(theme) => ({
              p: 1,
              backgroundColor: theme.palette.grey[0],
              borderBottom: `1px dashed ${theme.palette.grey[300]}`,
            })}
          >
            <DocumentsViewerNavigation
              docs={docs}
              currentDocumentIndex={currentDocumentIndex}
              setCurrentDocumentIndex={setCurrentDocumentIndex}
            />
          </Box>
        )}

        <Box
          ref={wrapper}
          sx={{
            width: '100%',
            height: isPdfRendererActive ? 'auto' : wrapperSize.height,
            minHeight: wrapperSize.height,
            backgroundColor: '#ffffff',
            '& #msdoc-renderer': {
              height: '100% !important',
              minHeight: `${wrapperSize.height}px !important`,
            },
            '& #msdoc-iframe': {
              width: '100% !important',
              height: '100% !important',
            },
          }}
        >
          {!isLoading &&
            currentDocument &&
            currentDocument.fileType &&
            DOC_VIEWER_DOCUMENT_EXTENSIONS.includes(currentDocument.fileType) && (
              <DocViewer
                documents={[currentDocument]}
                pluginRenderers={DocViewerRenderers}
                onDocumentChange={handleDocumentChange}
                prefetchMethod="GET"
                config={{
                  header: {
                    disableHeader: true,
                    disableFileName: true,
                    retainURLParams: false,
                  },
                }}
                style={{
                  /* React will convert numbers to px automatically */
                  width: wrapperSize.width,
                  height: wrapperSize.height,
                }}
              />
            )}

          {!isLoading &&
            currentDocument &&
            currentDocument.fileType &&
            !DOC_VIEWER_DOCUMENT_EXTENSIONS.includes(currentDocument.fileType) && (
              <DocumentsViewerPDFRenderer
                currentDocument={currentDocument}
                wrapperSize={wrapperSize}
                compact={isMobile && isZooming}
              />
            )}

          {isLoading && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                backgroundColor: '#fff',
              }}
            >
              <DocumentFileViewerSkeleton wrapperSize={wrapperSize} />
            </Box>
          )}
        </Box>

        <Box
          sx={(theme) => ({
            py: 1,
            borderBottomLeftRadius: '16px',
            borderBottomRightRadius: '16px',
            backgroundColor: theme.palette.grey[0],
            borderTop: `1px dashed ${theme.palette.grey[300]}`,
          })}
        />

        {!isZooming && (
          <Box
            sx={{
              position: 'absolute',
              bottom: 40,
              left: 20,
              zIndex: 9003,
              cursor: 'pointer',
            }}
          >
            <IconButton
              size="large"
              onClick={() => setIsZooming(true)}
              sx={{
                backgroundColor: '#fff',
                border: '1px solid rgba(0, 0, 0, 0.1)',
                boxShadow: '0px 0px 10px 2px rgba(0, 0, 0, 0.1)',
              }}
            >
              <ZoomInIcon fontSize="inherit" />
            </IconButton>
          </Box>
        )}
      </Box>
      {validationData && (
        <DocumentValidationReportModal
          data={validationData}
          handleClose={handleValidationReportClose}
        />
      )}
      {adocValidationData && (
        <DocumentAdocValidationReportModal
          data={adocValidationData}
          handleClose={handleValidationReportClose}
        />
      )}
    </>
  );
};

export default DocumentsViewer;
